/**
 * ErrorState Component
 * Error display with retry/back actions
 * Consistent error handling UI across the app
 */

import React from 'react';
import { Result, Button, Typography, Alert } from 'antd';
import {
  CloseCircleOutlined,
  WarningOutlined,
  ReloadOutlined,
  HomeOutlined,
} from '@ant-design/icons';
import { spacing } from '../../theme/designTokens';

const { Paragraph, Text } = Typography;

/**
 * ErrorState Component
 * Premium error messaging - calm, enterprise tone
 *
 * @param {Error|string} error - Error object or message
 * @param {string} title - Error title (default: "We couldn't load the data")
 * @param {string} description - Error description
 * @param {function} onRetry - Retry action handler
 * @param {function} onBack - Back/home action handler
 * @param {string} type - Error severity ('error' | 'warning' | 'info')
 * @param {boolean} showDetails - Show error details (default: false in production)
 * @param {Object} style - Additional styles
 */
function ErrorState({
  error,
  title = "We couldn't load the data",
  description = "Please try again",
  onRetry,
  onBack,
  type = 'error',
  showDetails = process.env.NODE_ENV === 'development',
  style = {},
}) {
  // Extract error message - calm, neutral tone
  const errorMessage = React.useMemo(() => {
    if (!error) return null;
    if (typeof error === 'string') return error;
    if (error.message) return error.message;
    if (error.response?.data?.message) return error.response.data.message;
    if (error.response?.statusText) return error.response.statusText;
    return 'An unexpected error occurred';
  }, [error]);

  // Extract error details (for development)
  const errorDetails = React.useMemo(() => {
    if (!error || typeof error === 'string') return null;
    if (error.stack) return error.stack;
    if (error.response?.data) return JSON.stringify(error.response.data, null, 2);
    return null;
  }, [error]);

  // Icon based on type
  const icon = type === 'error'
    ? <CloseCircleOutlined />
    : type === 'warning'
    ? <WarningOutlined />
    : null;

  // Status for Result component
  const status = type === 'error' ? 'error' : type === 'warning' ? 'warning' : 'info';

  return (
    <div style={{ paddingTop: 48, paddingBottom: 48, ...style }}>
      <Result
        status={status}
        icon={icon}
        title={title}
        subTitle={description || errorMessage}
        extra={
          <div>
            {/* Action buttons - calm, short text */}
            {(onRetry || onBack) && (
              <div style={{ marginBottom: spacing.md }}>
                {onRetry && (
                  <Button
                    type="primary"
                    icon={<ReloadOutlined />}
                    onClick={onRetry}
                    style={{ marginRight: spacing.sm }}
                  >
                    Retry
                  </Button>
                )}
                {onBack && (
                  <Button icon={<HomeOutlined />} onClick={onBack}>
                    Back
                  </Button>
                )}
              </div>
            )}

            {/* Error details (development only) */}
            {showDetails && errorDetails && (
              <Alert
                type="warning"
                message="Error Details (Development Only)"
                description={
                  <Paragraph>
                    <Text code copyable style={{ fontSize: '12px', whiteSpace: 'pre-wrap' }}>
                      {errorDetails}
                    </Text>
                  </Paragraph>
                }
                style={{ textAlign: 'left', marginTop: spacing.md }}
              />
            )}
          </div>
        }
      />
    </div>
  );
}

/**
 * ErrorBoundary Component
 * Catches React errors and displays ErrorState
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorState
          error={this.state.error}
          title="Something went wrong"
          description="Please refresh the page"
          onRetry={() => this.setState({ hasError: false, error: null })}
          onBack={() => window.location.href = '/'}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorState;
